import { useEffect, useMemo, useState, useCallback } from "react";
import dayjs from "dayjs";
import { Calendar, Views, dayjsLocalizer } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isDemoUser, simulateDemoAction } from "@/lib/demoMode";
import { toast } from "sonner";
import { ScheduleToolbar } from "@/components/schedule/ScheduleToolbar";
import { LessonDetailDialog } from "@/components/schedule/LessonDetailDialog";
import { CreateLessonDialog } from "@/components/schedule/CreateLessonDialog";

const localizer = dayjsLocalizer(dayjs);
const DnDCalendar = withDragAndDrop(Calendar);

const emptyForm = { type: "lesson", student_id: "", instructor_id: "", vehicle_id: "", date: "", start_time: "", end_time: "", notes: "", meeting_location: "" };

// react-big-calendar's Views enum maps to dayjs's add() unit names 1:1
// except for casing/day granularity — extracted to avoid a nested ternary.
const VIEW_TO_DAYJS_UNIT = { [Views.MONTH]: "month", [Views.WEEK]: "week", [Views.DAY]: "day" };

// Module-level constant (not recreated every render) — passed as-is into
// DnDCalendar's `views` prop below.
const CALENDAR_VIEWS = [Views.DAY, Views.WEEK, Views.MONTH, Views.AGENDA];

export default function Schedule() {
  const { user } = useAuth();
  const isStaff = user?.role === "admin" || user?.role === "office_staff";
  const [view, setView] = useState(Views.WEEK);
  const [date, setDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [students, setStudents] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [locations, setLocations] = useState([]);
  const activeLocations = useMemo(() => locations.filter((l) => l.active), [locations]);
  const [businessRules, setBusinessRules] = useState(null);
  const [resourceMode, setResourceMode] = useState("all"); // all | one
  const [selectedInstructor, setSelectedInstructor] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [overrideLocation, setOverrideLocation] = useState("");
  const [waiveFee, setWaiveFee] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [paymentHold, setPaymentHold] = useState(null);
  const [overrideNote, setOverrideNote] = useState("");
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);

  const loadAppointments = useCallback(async () => {
    try {
      const params = {};
      if (isStaff && resourceMode === "one" && selectedInstructor) params.instructor_id = selectedInstructor;
      const res = await api.get("/appointments", { params });
      setAppointments(res.data);
    } catch {
      toast.error("Could not load schedule");
    }
  }, [isStaff, resourceMode, selectedInstructor]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  useEffect(() => {
    if (isStaff) {
      api.get("/instructors").then((res) => setInstructors(res.data)).catch(() => {});
      api.get("/students").then((res) => setStudents(res.data)).catch(() => {});
      api.get("/vehicles").then((res) => setVehicles(res.data)).catch(() => {});
      api.get("/settings/meeting-locations").then((res) => setLocations(res.data)).catch(() => {});
      api.get("/settings/business-rules").then((res) => setBusinessRules(res.data)).catch(() => {});
    }
  }, [isStaff]);

  const events = useMemo(
    () =>
      appointments.map((a) => ({
        id: a.id,
        title: `${a.type === "lesson" ? a.student_name || "Lesson" : a.classroom_name || "Class"} \u2022 ${a.instructor_name || ""}`,
        start: new Date(a.start_time),
        end: new Date(a.end_time),
        resourceId: a.instructor_id,
        resource: a,
      })),
    [appointments]
  );

  const resources = useMemo(() => {
    if (!isStaff || resourceMode !== "all" || view === Views.MONTH || view === Views.AGENDA) return undefined;
    return instructors.filter((i) => i.status === "active").map((i) => ({ resourceId: i.id, resourceTitle: i.full_name }));
  }, [isStaff, resourceMode, instructors, view]);

  const eventPropGetter = useCallback((event) => {
    const status = event.resource?.status;
    return { className: `event-${status}` };
  }, []);

  const isPaymentHoldError = (err) => err?.response?.status === 402 && err?.response?.data?.detail?.code === "payment_hold";

  const onEventDrop = async ({ event, start, end, resourceId }) => {
    if (!isStaff) return;
    try {
      await api.put(`/appointments/${event.id}`, {
        instructor_id: resourceId || event.resource.instructor_id,
        start_time: dayjs(start).toISOString(),
        end_time: dayjs(end).toISOString(),
      });
      toast.success("Lesson moved");
      loadAppointments();
    } catch (err) {
      if (isPaymentHoldError(err)) {
        const detail = err.response.data.detail;
        toast.error(detail.message, {
          action: {
            label: "Full Override",
            onClick: async () => {
              try {
                await api.post(`/students/${detail.student_id}/payment-override`, { override_type: "full", note: "Granted from Schedule drag-and-drop reschedule" });
                await api.put(`/appointments/${event.id}`, {
                  instructor_id: resourceId || event.resource.instructor_id,
                  start_time: dayjs(start).toISOString(),
                  end_time: dayjs(end).toISOString(),
                });
                toast.success("Override granted — lesson moved");
                loadAppointments();
              } catch {
                toast.error("Could not grant override or move lesson");
              }
            },
          },
        });
      } else {
        toast.error(err?.response?.data?.detail || "Could not move - instructor or vehicle is already booked");
      }
      loadAppointments();
    }
  };

  const onSelectSlot = (slotInfo) => {
    if (!isStaff) return;
    setPaymentHold(null);
    setForm({
      ...emptyForm,
      instructor_id: slotInfo.resourceId || selectedInstructor || "",
      date: dayjs(slotInfo.start).format("YYYY-MM-DD"),
      start_time: dayjs(slotInfo.start).format("HH:mm"),
      end_time: dayjs(slotInfo.end).format("HH:mm"),
    });
    setCreateOpen(true);
  };

  const createAppointment = async () => {
    if (!form.instructor_id || !form.date || !form.start_time || !form.end_time) {
      toast.error("Please fill in instructor, date, and time");
      return;
    }
    if (form.type === "lesson" && !form.student_id) {
      toast.error("Please select a student for the lesson");
      return;
    }
    try {
      await api.post("/appointments", {
        type: form.type,
        student_id: form.type === "lesson" ? form.student_id : null,
        instructor_id: form.instructor_id,
        vehicle_id: form.vehicle_id || null,
        start_time: dayjs(`${form.date}T${form.start_time}`).toISOString(),
        end_time: dayjs(`${form.date}T${form.end_time}`).toISOString(),
        notes: form.notes,
        meeting_location: form.meeting_location || null,
      });
      toast.success("Lesson scheduled");
      setCreateOpen(false);
      setPaymentHold(null);
      setForm(emptyForm);
      loadAppointments();
    } catch (err) {
      if (isPaymentHoldError(err)) {
        setPaymentHold(err.response.data.detail);
      } else {
        toast.error(err?.response?.data?.detail || "Could not schedule - conflict detected");
      }
    }
  };

  const grantOverrideAndRetry = async (overrideType) => {
    if (!paymentHold) return;
    setOverrideSubmitting(true);
    try {
      await api.post(`/students/${paymentHold.student_id}/payment-override`, { override_type: overrideType, note: overrideNote || undefined });
      toast.success(overrideType === "full" ? "Full override granted" : "Courtesy override granted (allows one booking)");
      setOverrideNote("");
      await createAppointment();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not grant override");
    } finally {
      setOverrideSubmitting(false);
    }
  };

  const markStatus = async (status) => {
    try {
      await api.put(`/appointments/${selectedEvent.resource.id}/status`, { status });
      toast.success(`Marked as ${status.replace("_", " ")}`);
      setSelectedEvent(null);
      loadAppointments();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    }
  };

  const withinCancellationWindow = useMemo(() => {
    if (!selectedEvent || !businessRules) return false;
    const hoursUntil = dayjs(selectedEvent.resource.start_time).diff(dayjs(), "hour", true);
    return selectedEvent.resource.type === "lesson" && hoursUntil >= 0 && hoursUntil < (businessRules.cancellation_window_hours ?? 24);
  }, [selectedEvent, businessRules]);

  const cancelAppointment = async () => {
    if (isDemoUser(user)) {
      simulateDemoAction("this lesson would be cancelled instantly, with the student and parent notified — and any late-cancellation fee applied automatically per your Business Rules.");
      setSelectedEvent(null);
      setWaiveFee(false);
      return;
    }
    try {
      const res = await api.delete(`/appointments/${selectedEvent.resource.id}`, { params: { waive_fee: waiveFee } });
      if (res.data?.fee_applied) {
        toast.success(`Lesson cancelled. A $${res.data.fee_amount.toFixed(2)} late cancellation fee was applied to the student's account.`);
      } else {
        toast.success("Lesson cancelled");
      }
      setSelectedEvent(null);
      setWaiveFee(false);
      loadAppointments();
    } catch {
      toast.error("Could not cancel");
    }
  };

  const saveLocationOverride = async () => {
    if (!overrideLocation) return;
    try {
      await api.put(`/appointments/${selectedEvent.resource.id}`, { meeting_location: overrideLocation });
      toast.success("Meeting location updated — student and parent notified.");
      setOverrideLocation("");
      setSelectedEvent(null);
      loadAppointments();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update meeting location");
    }
  };

  const navigateDate = (dir) => {
    const unit = VIEW_TO_DAYJS_UNIT[view] || "day";
    setDate(dayjs(date).add(dir, unit).toDate());
  };

  const handleResourceChange = (value) => {
    if (value === "all") {
      setResourceMode("all");
      setSelectedInstructor("");
    } else {
      setResourceMode("one");
      setSelectedInstructor(value);
    }
  };

  return (
    <div className="space-y-4" data-testid="schedule-page">
      <ScheduleToolbar
        dateLabel={dayjs(date).format("MMMM D, YYYY")}
        onPrev={() => navigateDate(-1)}
        onNext={() => navigateDate(1)}
        onToday={() => setDate(new Date())}
        isStaff={isStaff}
        resourceMode={resourceMode}
        selectedInstructor={selectedInstructor}
        instructors={instructors}
        onResourceChange={handleResourceChange}
        view={view}
        onViewChange={setView}
        onNewLesson={() => { setForm(emptyForm); setPaymentHold(null); setCreateOpen(true); }}
      />

      <div className="rounded-[var(--radius)] border bg-card p-2 sm:p-3" style={{ height: "70vh" }}>
        <DnDCalendar
          localizer={localizer}
          events={events}
          date={date}
          onNavigate={setDate}
          view={view}
          onView={setView}
          views={CALENDAR_VIEWS}
          resources={resources}
          resourceIdAccessor="resourceId"
          resourceTitleAccessor="resourceTitle"
          selectable={isStaff}
          onSelectSlot={onSelectSlot}
          onSelectEvent={(e) => { setSelectedEvent(e); setOverrideLocation(""); setWaiveFee(false); }}
          onEventDrop={onEventDrop}
          onEventResize={onEventDrop}
          draggableAccessor={() => isStaff}
          resizable={isStaff}
          eventPropGetter={eventPropGetter}
          popup
          style={{ height: "100%" }}
          toolbar={false}
        />
      </div>

      <LessonDetailDialog
        selectedEvent={selectedEvent}
        onOpenChange={() => setSelectedEvent(null)}
        isStaff={isStaff}
        overrideLocation={overrideLocation}
        onOverrideLocationChange={setOverrideLocation}
        activeLocations={activeLocations}
        onSaveLocationOverride={saveLocationOverride}
        withinCancellationWindow={withinCancellationWindow}
        businessRules={businessRules}
        waiveFee={waiveFee}
        onWaiveFeeChange={setWaiveFee}
        onMarkStatus={markStatus}
        onCancelAppointment={cancelAppointment}
      />

      <CreateLessonDialog
        open={createOpen}
        onOpenChange={(v) => { setCreateOpen(v); if (!v) setPaymentHold(null); }}
        paymentHold={paymentHold}
        overrideNote={overrideNote}
        onOverrideNoteChange={setOverrideNote}
        overrideSubmitting={overrideSubmitting}
        onGrantOverrideAndRetry={grantOverrideAndRetry}
        form={form}
        onFormChange={setForm}
        students={students}
        instructors={instructors}
        vehicles={vehicles}
        businessRules={businessRules}
        activeLocations={activeLocations}
        onCreateAppointment={createAppointment}
      />
    </div>
  );
}
